import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import Loader from 'react-loader-spinner';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { useDraft2 } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { SsoConfig } from '../../../../api/ApiTypes';
import { Button, Colors, Input, remInPx } from '../../../../ui';
import { Checkbox } from '../../../../ui/Checkbox';
import { LoadingFrame } from '../../../../ui/LoadingGuard';
import { Notice } from '../../../../ui/Notice';
import { ResultInfo } from '../../../../ui/ResultInfo';
import { useLocationScope } from '../../../AppLocation';
import { useResolve } from '../../../Container';
import { AccountModel } from '../../../account/AccountModel';
import { EnableIfHasFeature, FeatureCallout, HideIfHasFeature } from '../../../common/FeatureElements';
import { ButtonStrip, Copy, Flexer, Title } from '../../../dashboard/elements/LayoutElements';
import { asyncOptionsPopupWith } from '../../../popups/OptionsPopup';
import { PopupManager } from '../../../popups/PopupManager';
import { UpgradeButton } from '../../../upgrade/UpgradeButton';
import { ManageFrame } from '../ManageFrame';
import { useSingleSignOnQuery } from './SingleSignOnHooks';

const restrictHelpText = `
    When this is enabled, members of the account will be prevented from logging in with any method other than the configured SSO identity provider.
`;

const confirmResetPopup = (execute: any) => asyncOptionsPopupWith(
    'Reset SSO Configuration',
    'Please confirm you want to reset your SSO configuration?  ' +
    'This will prevent your team from using Single Sign-On until it has been reconfigured.',
    [
        ['Reset', 'primary', execute],
    ]
);

const TextArea = styled.textarea<{ error?: boolean }>`
    display: block;
    position: relative;
    width: 100%;
    border-radius: 0.375rem;
    appearance: none;
    background-color: #ffffff;
    border-color: #d2d6dc;
    border-width: 1px;
    border-radius: 0.375rem;
    padding-top: 0.5rem;
    padding-right: 0.75rem;
    padding-bottom: 0.5rem;
    padding-left: 0.75rem;
    font-size: 1rem;
    line-height: 1.5;

    ${p => !p.error ? '' : css`
        border-color: ${Colors.negative};
    `}
`;

const ManageLegacySingleSignOnForm = React.memo<{ data: SsoConfig }>(({ data }) => {

    const account = useResolve(AccountModel);
    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const { draft, draftDirty, patchDraft, resetDraft, setDraft } = useDraft2(data);
    const [errors, setErrors] = useState({} as Record<string, boolean>);

    useEffect(() => resetDraft(data), [data]);

    const checkErrors = useCallback(() => {
        const results = [
            ['entryPoint', !draft.entryPoint?.trim()],
            ['issuer', !draft.issuer?.trim()],
            ['certificate', !draft.certificate?.trim()],
        ];
        setErrors(Object.fromEntries(results));
        return !!results.find(x => !!x[1]);
    }, [draft]);

    const save = useMutation({
        mutationKey: ['admin.sso.save'],
        mutationFn: async (body: SsoConfig) => api.exec(v2.admin.sso.save(body)),
        onSettled: () => {
            qc.invalidateQueries(['admin.sso.info']);
            resetDraft(draft);
        },
    });

    const reset = useMutation({
        mutationKey: ['admin.sso.reset'],
        mutationFn: async () => api.exec(v2.admin.sso.reset()),
        onSettled: () => {
            qc.invalidateQueries(['admin.sso.info']);
            resetDraft(draft);
        },
    });

    const onSave = useCallback(() => {
        if (checkErrors()) return;
        save.mutate(draft);
    }, [checkErrors, draft, save]);

    const onReset = useCallback(() => {
        popups.invoke(confirmResetPopup(() => {
            reset.mutate();
        }));
    }, []);

    return <Flexer flex="column" gap="2" maxWidth="64" padding="1.75 0">
        <Flexer flex="row" gap="1">
            <Notice icon="exclamation" color="red">
                <Copy>
                    <p>
                        Your SSO configuration requires an update to our improved SAML2 implementation.
                        The new version provides enhanced security and reliability. <Link to="~/manage/sso/setup">Click here to update your SSO configuration</Link> to take advantage of these improvements.
                    </p>
                </Copy>
            </Notice>
        </Flexer>

        <Flexer flex="column" gap="1">
            <Title>Legacy Identity Provider Settings</Title>
            <Input label="Entry Point" placeholder="e.g. https://example.okta.com/app/96034485_example/exkcqjdk2RClOiu/sso/saml"
                error={errors.entryPoint}
                message={errors.entryPoint ? 'Entry Point is required.' : ''}
                value={draft.entryPoint} onChange={x => patchDraft({ entryPoint: x })} />
            <Input label="Issuer" placeholder="e.g. http://www.okta.com/exkcqjdk2XGlOiuJm5d6"
                error={errors.issuer}
                message={errors.issuer ? 'Issuer is required.' : ''}
                value={draft.issuer} onChange={x => patchDraft({ issuer: x })} />
            <Input
                multiline={{ rows: 5 }}
                label="Certificate"
                value={draft.certificate}
                error={errors.certificate}
                message={errors.certificate ? 'Certificate is required.' : ''}
                onChange={x => patchDraft({ certificate: x })} />
            <Flexer>
                <Input.Label>Domains</Input.Label>
                <TextArea disabled rows={3} value={draft.domains} onChange={() => { }} />
                <Input.Hint>This setting must be configured by a member of our support team.</Input.Hint>
            </Flexer>
        </Flexer>

        <Checkbox
            label="Restrict login to Single Sign-On"
            helpText={restrictHelpText}
            checked={draft.required}
            onChange={x => patchDraft({ required: x })}
        />

        <ButtonStrip>
            <Button disabled={!draftDirty || save.isLoading} variant="primary" onAction={onSave}>
                {save.isLoading ? 'Saving...' : 'Save Configuration'}
                {save.isLoading && <Loader
                    type="TailSpin"
                    color="#fff"
                    height={remInPx(1.2)}
                    width={remInPx(1.2)}
                />}
            </Button>
            <Button disabled={save.isLoading} onAction={onReset}>
                Reset
            </Button>
        </ButtonStrip>
        {save.isSuccess && <ResultInfo mode="positive" message="Access control configuration updated." />}
        {save.isError && <ResultInfo mode="negative" message="Access control configuration failed to update.  Please contact support." />}
    </Flexer>;
});

export const ManageLegacySingleSignOnView = React.memo(() => {
    useLocationScope('sso', 'overview');

    const { isLoading, data } = useSingleSignOnQuery();

    return <ManageFrame title="Single Sign-On (SAML)">
        <HideIfHasFeature name="singleSignOn">
            <FeatureCallout maxWidth="64">
                <Title>This is a Premium feature.</Title>
                <Copy>In order to enable Single Sign-On (SAML), your organization must be a subscriber our Business plan.</Copy>
                <UpgradeButton variant="common" />
            </FeatureCallout>
        </HideIfHasFeature>
        {isLoading && <LoadingFrame />}
        {!isLoading && <>
            <EnableIfHasFeature name="singleSignOn">
                <ManageLegacySingleSignOnForm data={data} />
            </EnableIfHasFeature>
        </>}
    </ManageFrame>;
});
